
import {useRef, useState, useImperativeHandle, forwardRef, useLayoutEffect} from 'react';
import './PaymentActionBox.less';
import paymentApi from '../paymentApi';
import Transaction from '../../../entities/transaction';
import util from '../../../lib/util';
import IconButton from '../../../ui/IconButton/IconButton';
import Icons from '../../../ui/IconButton/Icons';
import OptionHelper from '../../../Configuration/GeneralOptions/OptionHelper';
import {connect} from 'react-redux';
import {sendEmails} from '../../../rest/api';
import Lead from '../../../entities/lead';
import Matter from '../../../entities/matter';
import FinancialHelper from '../../FinancialHelper';
import i18n from '../../../i18n/i18n';
import {formatFML, formatFMLFromId} from "../../../Contacts/ContactFormat";
import find from "../../../store/find";
import {getEmails} from "../../../lib/picos/getResourceTypes";
import {getCurrentFirm, isPortalUser} from "../../../lib/picos/currentLogin";
import RoutedIconButton from "../../../ui/Buttons/RoutedIconButton";
import * as TransactionActions from "../../../store/actions/transactionActions";
import CopyText from "../../../ui/CopyText";
import {getUserWhoProcessedTransaction} from "../../../rest/payrix";
import Alert from "../../../lib/alert";
import Contact from "../../../entities/contact";
import type {RootDispatch, RootState} from "../../../@types/store";
import type {PayrixPaymentMeta, PayrixTransactionFull} from "../../../@types/payrix";
import {ETransactionStatusName, ETransactionType, ZPayrixPaymentMeta} from "../../../@types/payrix";
import {isLawpayFormat, normalizePaymentMeta} from "../normalizePaymentMeta";
import {classifyPaymentForEmail} from "../paymentEmailClassify";
import {isCreditCard, TxnResultTypes} from "../Payrix/Payrix";
import type {EmailOptions, PaymentEmails} from "../../../@types/email";
import {ZEmailOptions, ZPaymentEmails} from "../../../@types/email";
import {existsFilter} from "../../../lib/picos/filters";
import type {PaymentMethod, ProcessorPaymentMeta} from "../../../@types/payments";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {
    PaymentAccountType,
    PaymentChargedOn,
    PaymentConsultName,
    PaymentDeposited,
    PaymentForCase,
    PaymentFund,
    PaymentHtmlSubject,
    PaymentNotes,
    PaymentPayerName,
    PaymentRefundCharge,
    PaymentRefunded,
    PaymentReplenish,
    PaymentTxDate,
    PaymentVoidedCharge
} from "../../../i18n/transaction";
import Loading from "../../../ui/Loading";
import useComponentCache from "../../../hooks/useComponentCache";
import useStateWithCallback from "../../../hooks/useStateWithCallback";

export type PaymentActionBoxProps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    {
        // How much the charge is/was for
        transaction: Transaction,

        // Payment Method
        paymentMethod?: PaymentMethod,

        // Which target account (processor's accounts) this transaction is for
        accountId: string,

        // If this is associated with a lead, what is the lead
        lead?: Lead,

        // If this is a case, what is the matter
        matter?: Matter,

        // After a charge method is called, send the metadata back to the parent
        onChange?: Function,

        // Call after a successful processing of a payment has been made
        onProcess?: Function,

        // Called if the payment was refused via LawPay
        onRefused?: Function,

        // Called when a refund is processed
        onRefunded?: Function,

        // Called to restore the payment
        onRestored?: Function,

        // Called if the payment was voided via LawPay
        onVoided?: Function,
    };

export type PaymentActionBoxState = {
    errOccurred: boolean,
    errMessage: string,
    isTxnProcessing: boolean,
    userProcessor: string,
    txn: PayrixTransactionFull | null,
}

export type PaymentActionBoxHandle = {
    onParentClose: () => void;
}

/**
 * This widget handles credit card and eCheck processing.  It has three methods that it calls on the server:
 * createCharge, voidCharge and refundCharge.  The props for this widget contain all of the information required.
 *
 * After any of these methods returns, we need to send three pieces of information back to the parent using the
 * onChange callback:
 * - processor: Which payment processor handled this transaction?  Get this from paymentApi.getName.
 * - processorPaymentId: The payment ID passed to us from the processor, e.g. pmtd_WesqHDqMXBn1RoI6qDTOu
 * - processorPaymentMeta: Any metadata passed from the processor along with the transaction (json)
 */
const PaymentActionBox = forwardRef<PaymentActionBoxHandle, PaymentActionBoxProps>((props, ref) => {
    const key = `payment-action-box-${props.transaction?.id || ''}`;
    const {cacheReset, cacheSave, cacheValue} = useComponentCache<PaymentActionBoxState>(key);

    const doResetRef = useRef<boolean>(false);

    const [errOccurred, setErrOccurred] = useState<boolean>(cacheValue('errOccurred', false));
    const [errMessage, setErrMessage] = useState<string>(cacheValue('errMessage', ''));
    const [userProcessor, setUserProcessor] = useState<string>(cacheValue('userProcessor', ''));
    const [isTxnProcessing, setIsTxnProcessing] = useStateWithCallback<boolean>(cacheValue('isTxnProcessing', false));
    const [txn, setTxn] = useState<PayrixTransactionFull>(cacheValue('txn', null));

    useImperativeHandle(ref, () => ({
        onParentClose: () => {
            doResetRef.current = true;
        }
    }), []);

    useLayoutEffect(() => {
        const doReset = doResetRef.current;

        return () => {
            if (doReset) cacheReset();
            else cacheSave({errOccurred, errMessage, userProcessor, isTxnProcessing: isTxnProcessing, txn});
        }
    }, [cacheReset, cacheSave, props.transaction, userProcessor, isTxnProcessing, txn, errOccurred, errMessage]);

    const fetchTxn = async (): Promise<PayrixTransactionFull> => {
        // Get any updates to the status of this transaction
        if (props.transaction.processorPaymentMeta?.id && paymentApi.isPayrix() && paymentApi.isConnected()) {
            let _txn = await paymentApi.getTransaction(props.transaction.processorPaymentMeta.id);

            // OWA-595: pass the local Transaction (not the PaymentMethod) — the Payrix
            // delegate uses it to rebuild a missing description (id/isTrust/notes) for
            // legacy txns. _txn is the Payrix txn for props.transaction.
            const meta = paymentApi.parsePaymentMeta(_txn, props.transaction);

            // Backfill voidedDate when the processor reports a past void/refund
            // the transaction hasn't recorded yet. These three writers are NOT
            // redundant — each covers a role the others don't, so do not "clean
            // up" one without checking the quadrant matrix in OWA-570:
            //   - onSaveTransactionQueue(tr): persists voidedDate via the queue.
            //   - onRefunded(meta) → PaymentCard.onPaymentRefunded → onChange(clone)
            //     (no save flag): the ONLY thing that updates the DISPLAYED txn on
            //     this path (TransactionCard.onCardChange ignores onChange(_, true)).
            //   - the onChange(meta) below → onPaymentActionChange persists via
            //     dispatch and updates other status fields, but is display-inert.
            // The !voidedDate guard here is deliberate: onPaymentActionChange's
            // deepEqual(meta) guard skips the unchanged-meta case this must catch.
            if (meta.voidOrRefundDate && !props.transaction.voidedDate) {
                const tr = props.transaction.clone();
                tr.voidedDate = meta.voidOrRefundDate;
                props.onSaveTransactionQueue(tr);
                props.onRefunded?.(meta);
            }

            try {
                let userName = await getUserWhoProcessedTransaction(props.transaction);
                setUserProcessor(userName);
            }
            catch (err) {
                console.error('Error fetching user who processed transaction:', err);
            }

            if (props.onChange) props.onChange(meta);

            return _txn;
        }
        else return null;
    }

    const txnQuery = useQuery({
        queryKey: [key, props.transaction?.addedOn, props.transaction?.dbAddedOn],
        queryFn: fetchTxn,
        placeholderData: keepPreviousData || null
    });

    /**
     * Get emails for each recipient.  Returns an object with keys for each email personal: payer, client(s), firm.
     * e.g. {
     *     clients: [{
     *       name: 'John Margaglione',
     *       email: 'john@margaglione.org'
     *     }],
     *     payer: {
     *       name: 'Jennifer Aniston',
     *       email: 'jennifer@aniston.pro'
     *     },
     *     firm: {
     *       name: 'Margaglione Law',
     *       email: 'me@outlaw.com'
     *     }
     * }
     *
     * @returns {PaymentEmails}
     */
    const getEmailInfo = (partial: boolean): PaymentEmails => {
        const {transaction} = props;

        const optionHelper = new OptionHelper();
        const emailPayer = optionHelper.getOptionValue(OptionHelper.EMAIL_PAYER);
        const emailClient = optionHelper.getOptionValue(OptionHelper.EMAIL_CLIENT);
        const emailSelf = optionHelper.getOptionValue(OptionHelper.EMAIL_SELF);
        const firmEmail = getCurrentFirm().sendToEmail;
        // const firmCC = getCurrentFirm().ccToEmail;
        // const firmBCC = getCurrentFirm().bccToEmail;

        const targets: Partial<PaymentEmails> = {};

        if (emailSelf && firmEmail) targets.firm = {
            name: getCurrentFirm().billingName || getCurrentFirm().legalName,
            email: firmEmail
        };

        if (emailPayer) {
            // Is there a separate payer?
            const payer = !transaction.clientIds.includes(transaction.source);
            const email = getEmails(find<Contact>('contacts', transaction.source))?.find(e => e.isPrimary)?.value;
            if (payer && email) targets.payer = {
                name: formatFMLFromId(transaction.source),
                email
            }
        }

        if (emailClient) {
            const clientEmails = transaction.clientIds
                .map(cid => find<Contact>('contacts', cid)) // contacts
                .filter(existsFilter)
                .map(c => {
                    const email = getEmails(c).find(e => e.isPrimary);

                    if (email) return {
                        name: formatFML(c),
                        email: email.value
                    }
                    else return null;
                    // }
                }) // emails
                .filter(util.existsFilter)

            if (clientEmails.length > 0) targets.clients = clientEmails;
        }

        const parsedTargets = partial ? ZPaymentEmails.partial().safeParse(targets) : ZPaymentEmails.safeParse(targets);
        if (parsedTargets.error) console.error('Failed parsing ZEmailInfo', parsedTargets, targets);

        return targets as PaymentEmails;
    }

    /**
     * Send emails to the payer, client and firm (if requested).
     *
     * @param {ProcessorPaymentMeta} meta - parsed metadata
     * @returns {Promise<string>}
     */
    const _sendEmails = async (meta: ProcessorPaymentMeta): Promise<string> => {
        const parseResult = ZPayrixPaymentMeta.safeParse(meta);
        if (parseResult.error) {
            console.error(parseResult.error, meta);
            Alert.addSimpleError(parseResult.error.message);
        }

        // For easier access to props
        const {transaction, matter, lead} = props;
        const isTrustRelated = transaction.txType === 'FR';
        const isReplenishment = isTrustRelated &&
            FinancialHelper.getTransactions(transaction.clientIds, [matter.id])
                .filter(tr => tr.txType === 'FR' && tr.id !== transaction.id)
                .length > 0;
        const isCaseTrust = isTrustRelated && transaction.targetType === 'M';
        const clientNames = matter?.clients.map(cid => formatFMLFromId(cid, false)) ||
            lead?.clients.map(cid => formatFMLFromId(cid, false));
        const firm = getCurrentFirm();
        const billingName = firm.billingName || firm.legalName;

        // Convert metadata to sane variables
        const {
            amount,
            amount_refunded,
            chargeDate,
            voidOrRefundDate,
            method,
            payment
        } = meta;

        const paymentMethod = /*payment ||*/ method;

        // A Payrix void surfaces as statusText === Voided (not a CreditCardReverseAuth type).
        // Keying off the old LawPay-era ReverseAuth check emailed clients a "Thank you for your
        // payment!" receipt when they voided a charge. See classifyPaymentForEmail. (OWA-508)
        const {isVoid, isRefund} = classifyPaymentForEmail(meta);

        // Email addresses to send to
        const emails = getEmailInfo(false); // We will send them all on the TO line.
        /* TODO: Why isn't this being used? */
        const emailAddresses = Object.values(emails).flat().map(o => o.email);

        /* Now we need to format the email message.  We will need:
           - What is the purpose of this payment?
               a. Is this a payment for an invoice? If so, which invoice (or payment for a CSM?)
               b. Is this payment to fund or replenish a trust? If so, is this a client or case trust?
               c. Was this a voided transaction?
               d. Was this a full or partial refund?  If partial, how much?
           - Which lead/case is this payment related to?  Use case name or code?  Use lead name or something else?
           - What was the amount of the transaction?
           - Name of the payer (if not the client)
           - Name of the client
           - Type of account (card or bank)
             a. If a card, what card type (e.g. Mastercard), and what is the last four of the card?
             b. If a bank, what type of account (e.g. checking), and what are the last four of the account #?
           - Notes from the transaction card.
         */

        // Here are the variables to be filled in:

        // Trust name
        const trustName = isCaseTrust ? i18n.ls('transaction', 'pmtCaseTrust')
                                      : i18n.ls('transaction', 'pmtGeneralTrust');

        const isPartial = isRefund ? amount_refunded < amount : false;
        // const partialFmt = isPartial ? util.formatCurrency(amount_refunded, true) : null;

        // Subject of the email (also the first line of the text).
        // e.g. Thank you for your payment!
        //      Thank you for funding your trust account!
        //      Thank you for replenishing your trust account!
        const subject = isVoid ? i18n.ls('transaction', 'pmtVoided')
                               : isRefund ? i18n.ls('transaction', 'pmtRefunded', {isPartial} as PaymentRefunded)
                                          : isTrustRelated
                                            ? isReplenishment
                                              ? i18n.ls('transaction', 'pmtReplenish', {trustName} as PaymentReplenish)
                                              : i18n.ls('transaction', 'pmtFund', {trustName} as PaymentFund)
                                            : i18n.ls('transaction', 'pmtThankYou');

        // e.g. 10/11/2021 at 03:21pm
        const paymentDate = i18n.ls('transaction', 'pmtTxDate', {
            txDate: chargeDate.toLocaleDateString('en-US'),
            txTime: chargeDate.toLocaleTimeString('en-US').toLowerCase()
        } as PaymentTxDate);

        const voidOrRefundOn = isVoid || isRefund
                               ? i18n.ls('transaction', 'pmtTxDate', {
                txDate: voidOrRefundDate ? voidOrRefundDate.toLocaleDateString('en-US') : null,
                txTime: voidOrRefundDate ? voidOrRefundDate.toLocaleTimeString('en-US').toLowerCase() : null
            } as PaymentTxDate)
                               : null;

        // e.g. $1000.25
        const amountFmt = util.formatCurrency(amount, true);

        // e.g. Mastercard or Visa (card), checking or savings (bank)
        let methodName = paymentMethod.type === 'card' || (payment && isCreditCard(payment.method))
                         ? paymentMethod.cardType
                         : i18n.ls('transaction', 'pmtAccountType', {accountType: paymentMethod.accountType} as PaymentAccountType);

        // Name of the payer, will be "your" if no benefactor, or the benefactor's full name
        const payerName = emails.payer
                          ? i18n.ls('transaction', 'pmtPayerName', {name: emails.payer.name} as PaymentPayerName)
                          : i18n.c('your');

        // Name of the case, case code, or lead's case types, e.g. 'Divorce with Children', '2021-D-0001'
        let issueName = isCaseTrust && matter
                        ? i18n.ls('transaction', 'pmtForCase', {code: matter.code} as PaymentForCase)
                        : '';

        // Notes
        const comment = transaction.notes;

        // Name for initial consults
        const consultName = new OptionHelper().getOptionValue(OptionHelper.PAID_CONSULT_NAME) || i18n.c('initialConsult');

        // The text version of the email
        let text = `re: ${clientNames.join('; ')}\n\n`;

        text += `${subject}\n\n`;

        if (isVoid) text += i18n.ls('transaction', 'pmtVoidedCharge', {
            voidOrRefundOn,
            billingName,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentVoidedCharge) + '\n\n';
        else if (isRefund) text += i18n.ls('transaction', 'pmtRefundCharge', {
            voidOrRefundOn,
            billingName,
            isPartial,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentRefundCharge) + '\n\n';
        else text += i18n.ls('transaction', 'pmtChargedOn', {
                paymentDate,
                billingName,
                amountFmt,
                payerName,
                methodName,
                methodNumber: paymentMethod.number
            } as PaymentChargedOn) + '\n\n';

        if (!isVoid && !isRefund) {
            if (isTrustRelated) text += i18n.ls('transaction', 'pmtDeposited', {
                trustName, issueName
            } as PaymentDeposited) + '\n\n';
            else if (matter) text += i18n.ls('transaction', 'pmtAppliedTo') + '\n\n';
            else text += i18n.ls('transaction', 'pmtConsultName', {consultName} as PaymentConsultName) + '\n\n';
        }

        if (comment) text += i18n.ls('transaction', 'pmtNotes', {
            billingName,
            comment
        } as PaymentNotes) + '\n\n';

        text += i18n.ls('transaction', 'pmtThankYouBusiness');

        // The html version of the email
        let html = `<p>re: ${clientNames.join('; ')}</p><br/>`;

        html += i18n.ls('transaction', 'pmtHtmlSubject', {subject} as PaymentHtmlSubject);

        if (isVoid) html += `<p>${i18n.ls('transaction', 'pmtVoidedCharge', {
            voidOrRefundOn,
            billingName,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentVoidedCharge)}</p>`;
        else if (isRefund) html += `<p>${i18n.ls('transaction', 'pmtRefundCharge', {
            voidOrRefundOn,
            billingName,
            isPartial,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentRefundCharge)}</p>`;
        else html += `<p>${i18n.ls('transaction', 'pmtChargedOn', {
                paymentDate,
                billingName,
                amountFmt,
                payerName,
                methodName,
                methodNumber: paymentMethod.number
            } as PaymentChargedOn)}</p>`;

        if (!isVoid && !isRefund) {
            if (isTrustRelated) html += `<p>${i18n.ls('transaction', 'pmtDeposited', {
                trustName, issueName
            } as PaymentDeposited)}</p>`;
            else if (matter) html += `<p>${i18n.ls('transaction', 'pmtAppliedTo')}</p>`;
            else html += `${i18n.ls('transaction', 'pmtConsultName', {consultName} as PaymentConsultName)}</p>`;
        }

        if (comment) html += i18n.ls('transaction', 'pmtNotes', {
            billingName,
            comment
        } as PaymentNotes);

        html += `<br/><p>${i18n.ls('transaction', 'pmtThankYouBusiness')}</p>`;

        const to = emails.clients.map(e => e.email);
        if (emails.payer?.email) to.push(emails.payer.email);
        const cc = emails.firm?.email;

        try {
            const options: EmailOptions = {
                from: 'payments@outlawpayments.com',
                to, // Clients and payers
                cc: [cc], // The firm, if requested
                subject,
                text,
                html,
                replyTo: 'no-reply@outlawpractice.com',
            };

            const parsedOptions = ZEmailOptions.safeParse(options);
            if (parsedOptions.error) console.error('Error parsing email options', parsedOptions);

            const emailResult = await sendEmails(options);
            if (!emailResult.isOk()) Alert.addSimpleError(`Error sending email to client or payer: ${emailResult.unwrapErr().message}`);
        }
        catch (err) {
            console.error('Error sending email to client', err);

            Alert.addSimpleError(`Error sending email to client or payer: ${err.message}`);
        }

        // if (firm.sendToEmail || firm.ccToEmail || firm.bccToEmail) {
        //     try {
        //         const options = {
        //             from: 'payments@outlawpayments.com',
        //             to: firm.sendToEmail,
        //             cc: firm.ccToEmail,
        //             bcc: firm.bccToEmail,
        //             subject,
        //             text,
        //             html,
        //             replyTo: 'no-reply@outlawpractice.com'
        //         };
        //         const parsedOptions = ZEmailOptions.safeParse(options);
        //         if (parsedOptions.error) console.error('Failed to parse options for sendEmails', parsedOptions);
        //         await sendEmails(options);
        //     }
        //     catch (err) {
        //         console.error('Error sending email(s) to the firm', err);
        //         Alert.addSimpleError(`Error sending email(s) to your firm: ${err.status} - ${err.message}`);
        //     }
        // }

        return text;
    }

    type SyncProcessingCallback = () => void;
    type AsyncProcessingCallback = () => void | Promise<void>;
    type SetProcessingCallback = SyncProcessingCallback | AsyncProcessingCallback;

    const isProcessing = () => sessionStorage.getItem('processingPayment') === 'true';

    /**
     * It is imperative that calls to onProcess always return successfully if the call to the paymentsApi return
     * successfully.  In other words, errors that occur processing the response from the API should NEVER stop
     * this function from returning a positive response. Otherwise, the UI gets out of sync with the payment, and
     * the user will be unable to save a transaction that processed successfully on the server!
     */
    const onProcess = async () => {
        if (isTxnProcessing) return;

        setErrOccurred(false);
        setErrMessage('');
        setIsTxnProcessing(true, async () => {
            const {paymentMethod, amount} = props.transaction;

            // Call the server to create the payment. It will either return an Error or a PaymentTransactionFull
            const paymentResult = await paymentApi.createPayment(paymentMethod, amount, props.accountId, props.transaction);
            if (paymentResult.isErr()) {
                const err = paymentResult.unwrapErr();

                console.warn('💳 createPayment failed', err);

                // Spit out the error for logging purposes
                console.error(err);

                // Set the error in state
                setErrOccurred(true);
                setErrMessage(err.message);

                // Refused by the server
                props.onRefused?.();

                setIsTxnProcessing(false);
            }
            else {
                const txn = paymentResult.unwrap();

                // Tell the app we finished processing with no errors
                setIsTxnProcessing(false);
                setErrOccurred(false);
                setErrMessage('');
                setTxn(txn);

                // Parse the payment metadata. Could throw an error!
                try {
                    // Parse the payment meta data
                    const cMeta = paymentApi.parsePaymentMeta(txn, props.transaction);

                    // Let the payment page know we have processed the payment
                    props.onProcess?.(cMeta); // Does not throw errors

                    // Let the payment page know the results
                    props.onChange?.(cMeta); // Does not throw errors

                    // Now send the emails
                    _sendEmails(cMeta)
                        .catch(err => {
                            Alert.addSimpleError('The transaction processed successfully, but there was a problem sending emails. ' +
                                'You will need to manually send an email to the client letting them know that their payment went through. ' +
                                `Error: ${err.message}`
                            );
                        });
                }
                catch (err) {
                    console.error('Unable to parse payment meta:', err);

                    Alert.addSimpleError('The transaction processed successfully, but there was a problem sending emails. ' +
                        'You will need to manually send an email to the client letting them know that their payment went through. ' +
                        `Error: ${err.message}`
                    );
                }
            }
        });
    }

    const onRefund = () => {
        if (isTxnProcessing) return;
        setIsTxnProcessing(true, () => {
            const paymentMeta = normalizePaymentMeta(props.transaction.processorPaymentMeta);

            paymentApi.refundTransaction(paymentMeta?.id, paymentMeta?.amount || props.transaction.amount)
                .then(txn => {
                    try {
                        const cMeta = paymentApi.parsePaymentMeta(txn, props.transaction);

                        if (props.onChange) props.onChange(cMeta); // Can not throw errors

                        setErrOccurred(false);
                        setErrMessage('');

                        props.onRefunded?.(cMeta); // Can not throw errors

                        // Now send the emails
                        _sendEmails(cMeta)
                            .catch(err => {
                                console.error('Unable to parse payment meta:', err);
                                Alert.addSimpleError('The refund processed successfully, but there was a problem sending emails. ' +
                                    'You will need to manually send an email to the client letting them know that the refund went through. ' +
                                    `Error: ${err.message}`
                                );
                            })
                    }
                    catch (err) {
                        console.error('Unable to parse payment meta:', err);
                        Alert.addSimpleError('The refund processed successfully, but there was a problem sending emails. ' +
                            'You will need to manually send an email to the client letting them know that the refund went through. ' +
                            `Error: ${err.message}`
                        );
                    }
                })
                .catch(err => {
                    console.error(err);
                    setErrOccurred(true);
                    setErrMessage(err.message);
                })
                .finally(() => {
                    setIsTxnProcessing(false);
                });
        });
    }

    const onVoid = () => {
        if (isTxnProcessing) return;

        setIsTxnProcessing(true, () => {
            // Normalize the processor meta so we use the canonical id field
            // regardless of whether the meta is in Payrix or LawPay shape.
            const paymentMeta = normalizePaymentMeta(props.transaction.processorPaymentMeta);
            paymentApi.voidTransaction(paymentMeta?.id)
                .then(txn => {
                    try {
                        const cMeta = paymentApi.parsePaymentMeta(txn, props.transaction);
                        if (props.onChange) props.onChange(cMeta);
                        setErrOccurred(false);
                        setErrMessage('');

                        // If we voided a payment, call onVoided
                        props.onVoided?.(cMeta);

                        // If we voided a refund, reinstate
                        // props.onRestored?.(cMeta);

                        // Now send the emails
                        _sendEmails(cMeta)
                            .catch(err => {
                                console.error('Unable to parse payment meta:', err);
                                Alert.addSimpleError('The payment was successfully voided, but there was a problem sending emails. ' +
                                    'You will need to manually send an email to the client letting them know that the payment was voided. ' +
                                    `Error: ${err.message}`
                                );
                            })
                    }
                    catch (err) {
                        console.error('Unable to parse payment meta:', err);
                        Alert.addSimpleError('The payment was successfully voided, but there was a problem sending emails. ' +
                            'You will need to manually send an email to the client letting them know that the payment was voided. ' +
                            `Error: ${err.message}`
                        );
                    }
                })
                .catch(err => {
                    console.error(err);
                    setErrOccurred(true);
                    setErrMessage(err.clientMessage || err.message);
                })
                .finally(() => {
                    setIsTxnProcessing(false);
                });
        });
    }

    const img = paymentApi.getIcon();
    const tr = props.transaction;
    let authCode: string | null;

    if (tr) {
        authCode = normalizePaymentMeta(tr.processorPaymentMeta)?.authorization ?? null;
    }

    const hasTxn = !!props.transaction.processorPaymentMeta?.id;
    if (hasTxn && txnQuery.isLoading) return <Loading message="Loading transaction"/>;
    if (hasTxn && txnQuery.isError || txnQuery.isLoadingError)
        return <div className='u-errorOccurred'>{txnQuery.error.toString()}</div>;

    // We have to consider the combination of status and type when determining what is going on with transactions.
    // This is vendor-specific, so we call the paymentApi for help.
    // For LawPay transactions, txnQuery.data is null (API no longer exists), so derive flags from stored metadata.
    let flags: string[], message: string;
    if (hasTxn && isLawpayFormat(tr.processorPaymentMeta)) {
        const lpMeta = normalizePaymentMeta(tr.processorPaymentMeta);
        if (lpMeta) {
            const isRefund = [ETransactionType.ECheckRefund, ETransactionType.CreditCardRefund].includes(lpMeta.type);
            const isVoided = lpMeta.statusText === ETransactionStatusName.Voided;
            const amt = util.formatCurrency(lpMeta.amount, true);
            if (isRefund) {
                flags = ['REFUND', 'COMPLETE'];
                message = `Refund of ${amt} was processed via LawPay.`;
            } else if (isVoided) {
                flags = ['VOID', 'COMPLETE'];
                message = `Payment of ${amt} was voided via LawPay.`;
            } else {
                flags = ['CHARGE', 'COMPLETE'];
                message = `Payment of ${amt} was processed via LawPay.`;
            }
        } else {
            flags = [];
            message = '';
        }
    } else {
        ({flags, message} = hasTxn ? paymentApi.getCurrentStatus(!txnQuery.isLoading && txnQuery.data) : {flags: [], message: ''});
    }
    const hasFlags = flags.length > 0;
    const canVoid = flags.includes('INCOMPLETE') && !flags.includes('REFUND') && !flags.includes('REFUNDED');
    const canRefund = flags.includes('COMPLETE') && flags.includes('CHARGE');
    const canCharge = flags.length === 0;
    const emailInfo = getEmailInfo(true);
    const emails = Object.keys(emailInfo).map(k => {
        const o = emailInfo[k];

        if (Array.isArray(o)) return o.map(obj => `${obj.email} (client)`);
        else return `${o.email} (${k})`;

    }).flat().filter(util.existsFilter);
    const hasEmails = emails.length > 0;
    const meta = normalizePaymentMeta(tr.processorPaymentMeta);

    return (
        <div className='payment-action-box'>
            <div className='fieldset'>
                {/* Processor Logo */}
                <h1>
                    {/* Processor Logo */}
                    {/* @ts-ignore */}
                    <img src={img} height={32} alt={paymentApi.getName()}/>

                    {/* Let them know where the emails will be sent (if any), and a link to the config */}
                    {!isPortalUser() && <RoutedIconButton type={Icons.CONFIG}
					                                      title={i18n.ls('transaction', 'pmtConfigurePayments')}
					                                      label={i18n.ls('transaction', 'pmtConfigure')}
					                                      route='/config/financial/banking'
					/>}
                </h1>

                <div className='payment-action-box__content'>

                    {/* Status */}
                    <div className='payment-action-box__status'>
                        {/* Status Message */}
                        {message && <div style={{marginTop: '1em', fontWeight: 600}}>{message}</div>}

                        {/* Type */}
                        {!isPortalUser() && meta?.typeText && meta.voidOrRefundDate &&
							<div style={{marginTop: '1em'}}>
								<label style={{fontWeight: 600}}>{meta.typeText}</label> on {meta.voidOrRefundDate.toLocaleString('en-US')}
							</div>
                        }

                        {/* Emails */}
                        {hasEmails && <div style={{marginTop: '5px'}}>
							<div style={{
                                marginTop: '1em',
                                fontWeight: 600
                            }}>{i18n.ls('transaction', txn ? 'pmtEmailsSent' : 'pmtEmails')}</div>
							<ul>
                                {emails.map(e => <li key={e}>{e}</li>)}
							</ul>
						</div>
                        }

                        {/* Who ran the payment? */}
                        {!isPortalUser() && userProcessor && <div style={{marginTop: '1em'}}>
							<label style={{fontWeight: '600', marginRight: '5px'}}>
                                {i18n.ls('transaction', 'pmtProcessorPaymentUser')}
							</label>
							<span>{userProcessor || ''}</span>
						</div>}

                        {/* Authorization Code (if available) */}
                        {hasFlags && authCode && <div style={{marginTop: '1em'}}>
							<CopyText label={i18n.ls('transaction', 'pmtAuthCode')}
							          text={authCode}
							          useCodeStyle={true}
							/>
						</div>
                        }

                        {/* Payment ID */}
                        {!isPortalUser() && meta?.id && <div style={{marginTop: '1em'}}>
							<CopyText text={meta?.id}
							          label={i18n.ls('transaction', 'pmtPaymentId')}
							          useCodeStyle={true}
							/>
						</div>}

                        {/* Show the transaction results */}
                        {txn && <ul>
                            {txn.txnResults?.map((txnResults) => {
                                return <li key={txnResults.id}>
                                    {txnResults.created} [{TxnResultTypes[txnResults.code]}] [{txnResults.bankCode}] {txnResults.message}
                                </li>
                            })}
						</ul>
                        }

                    </div>

                    {/* Action Buttons */}
                    <div className='payment-action-box__buttons'>
                        {/* Process Button */}
                        {!meta?.chargeDate && canCharge && tr.amount && tr.paymentMethod &&
							<IconButton type={Icons.CHARGE}
							            label={i18n.ls('transaction', isPortalUser()
                                                                      ? 'pmtProcess_portal'
                                                                      : 'pmtProcess'
                                        )}
							            labelBold={true}
							            onClick={onProcess}
							            disabled={isTxnProcessing}
							            color='darkgreen'
							            textColor='darkgreen'
							/>
                        }

                        {/* Void Button */}
                        {!isPortalUser() && canVoid &&
							<IconButton type={Icons.VOID}
							            label={i18n.ls('transaction', 'pmtVoid')}
							            labelBold={true}
							            onClick={onVoid}
							            disabled={isTxnProcessing}
							            textColor='maroon'
							/>}

                        {/* Refund Button */}
                        {!isPortalUser() && canRefund &&
							<IconButton type={Icons.REFUND}
							            label={i18n.ls('transaction', 'pmtRefund')}
							            labelBold={true}
							            color='maroon'
							            onClick={onRefund}
							            disabled={isTxnProcessing}
							            textColor='maroon'
							/>}
                    </div>

                    {/* Error messages */}
                    {errOccurred && <div className='payment-action-box__error'>
                        {errMessage || ''}
					</div>
                    }

                </div>
            </div>

        </div>
    );
});

PaymentActionBox.displayName = 'PaymentActionBox';

const mapStateToProps = (state: RootState) => {
    return {
        resources: state.resources,
        transactions: state.transactions,
    };
};

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onSaveTransactionQueue: tr => dispatch(TransactionActions.saveTransactionQueue(tr)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(PaymentActionBox);
